import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JupiterClientEnvironment } from 'constants/Chat.constants';
import { DEPLOYMENTS_QUERY_KEY } from 'constants/QueryKeys.constants';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import { closeSidePanel, resetChat } from 'actions';
import api from 'api';
import type { HTTPError } from 'api/error';
import type { ProjectWithTokenExceeded } from 'api/types';
import { curryPostFunction } from 'api/utils';
import { getProjectQueryKey } from 'utils/query';
import { useLocalDispatch } from './reduxHooks';
import useSampleProject from './useSampleProjects/useSampleProject';
import { useToast } from './useToast';

export const PROJECT_MUTATION_KEY = 'project';

const useProject = () => {
  const { accountId, projectId } = useRequiredParams<{
    accountId: string;
    projectId: string;
  }>();
  const { inSampleProject } = useSampleProject();

  const dispatch = useLocalDispatch();

  const queryKey = getProjectQueryKey(accountId, projectId);
  const queryClient = useQueryClient();

  const toast = useToast();

  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isProjectError,
    error: projectError,
  } = useQuery<ProjectWithTokenExceeded, HTTPError, ProjectWithTokenExceeded>({
    queryKey,
    queryFn: () => api.getProject(accountId, projectId),
    enabled: !inSampleProject && !!accountId && !!projectId,
  });

  const resetDraftChat = () => {
    dispatch(resetChat({ clientEnv: JupiterClientEnvironment.DRAFT }));
  };

  const resetChats = () => {
    dispatch(resetChat({}));
  };

  const {
    mutate: publishDraft,
    isLoading: isPublishingDraft,
    isSuccess: isPublishSuccess,
  } = useMutation(
    curryPostFunction(api.publishDraft)({ accountId, projectId }),
    {
      mutationKey: [PROJECT_MUTATION_KEY],
      onSuccess: () => {
        // Publish deploys the draft but does not modify agent config, so the
        // projection query must NOT be refetched here. Only project metadata
        // and deployments are affected.
        queryClient
          .refetchQueries(getProjectQueryKey(accountId, projectId))
          .catch(e =>
            console.error('Error occurred. Could not refetch queries', e),
          );
        queryClient
          .invalidateQueries([accountId, projectId, DEPLOYMENTS_QUERY_KEY])
          .catch(e =>
            console.error(
              'Error occurred. Could not invalidate deployments',
              e,
            ),
          );
        resetChats();
        toast.success({
          title: 'Agent published',
        });
      },
      onError: () => toast.error({ title: 'Failed to publish draft' }),
    },
  );

  const { mutate: deleteDraft, isLoading: isDeletingDraft } = useMutation(
    () => api.deleteDraft({ accountId, projectId }),
    {
      mutationKey: [PROJECT_MUTATION_KEY],
      onSuccess: () => {
        // The stream's setBranchId handler resets to main branch and triggers
        // a projection re-fetch automatically. Only invalidate project metadata here.
        queryClient
          .invalidateQueries(getProjectQueryKey(accountId, projectId))
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        resetDraftChat();
        dispatch(closeSidePanel());
        toast.success({
          title: 'Draft deleted',
        });
      },
    },
  );

  return {
    project,
    isLoadingProject,
    isProjectError,
    isProjectForbidden: projectError?.code === 'UserAccessForbidden',
    projectError,
    projectQueryKey: queryKey,
    accountId,
    projectId,
    publishDraft,
    isPublishingDraft,
    isPublishSuccess,
    deleteDraft,
    isDeletingDraft,
  };
};

export default useProject;
